import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import apiClient from '../services/api';
import { DashboardStats, SystemHealth, AgentPrompt, ConversationSummary, UserManagement, AdvancedAnalytics } from '../types';
import AnalyticsCard from '../components/Admin/AnalyticsCard';
import PromptEditor from '../components/Admin/PromptEditor';
import './AdminPage.css';

const AdminPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { setNavigationContext } = useNavigation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [advancedAnalytics, setAdvancedAnalytics] = useState<AdvancedAnalytics | null>(null);
  const [prompts, setPrompts] = useState<AgentPrompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<AgentPrompt | null>(null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [users, setUsers] = useState<UserManagement[]>([]);
  const [activeTab, setActiveTab] = useState<'analytics' | 'prompts' | 'users'>('analytics');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showAllConversations, setShowAllConversations] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState<'overview' | 'flow'>('overview');
  const [analyticsRange, setAnalyticsRange] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
  const [conversationQuery, setConversationQuery] = useState('');
  const [promptCategoryFilter, setPromptCategoryFilter] = useState<string>('all');
  const [userRoleFilter, setUserRoleFilter] = useState<'all' | 'admin' | 'user'>('all');
  const [userStatusFilter, setUserStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    is_admin: false,
    is_active: true
  });

  const getFriendlyErrorMessage = useCallback((err: any, fallbackMessage: string) => {
    const statusCode = err?.status_code ?? err?.response?.status;
    const detailRaw = err?.detail ?? err?.response?.data?.detail;

    const translateValidationMessage = (message: string) => {
      if (!message) return message;

      const minLengthMatch = message.match(/^String should have at least (\d+) characters$/i);
      if (minLengthMatch) {
        return `Debe tener al menos ${minLengthMatch[1]} caracteres.`;
      }

      if (/field required/i.test(message)) {
        return 'Este campo es obligatorio.';
      }

      return message;
    };

    const humanizeField = (field: string) => {
      const labels: Record<string, string> = {
        email: 'Correo electrónico',
        password: 'Contraseña',
        is_admin: 'Rol de administrador',
        is_active: 'Estado del usuario',
      };
      return labels[field] || field;
    };

    const parseDetail = (raw: any): string => {
      if (!raw) return '';

      if (typeof raw === 'string') {
        return raw.trim();
      }

      if (Array.isArray(raw)) {
        const first = raw[0];
        if (!first) return '';

        if (typeof first === 'string') {
          return first;
        }

        if (typeof first === 'object' && first.msg) {
          const loc = Array.isArray(first.loc) ? first.loc : [];
          const field = loc.length > 0 ? String(loc[loc.length - 1]) : '';
          const translatedMsg = translateValidationMessage(String(first.msg));
          return field
            ? `${humanizeField(field)}: ${translatedMsg}`
            : translatedMsg;
        }

        return 'Los datos enviados no son válidos.';
      }

      if (typeof raw === 'object') {
        if (typeof raw.detail === 'string') return raw.detail;
        if (typeof raw.message === 'string') return raw.message;
      }

      return String(raw).trim();
    };

    const detail = parseDetail(detailRaw);
    const detailLower = detail.toLowerCase();

    if (statusCode === 400 && detailLower.includes('password')) {
      return 'La contraseña no cumple los requisitos mínimos. Usa una de al menos 8 caracteres.';
    }

    if (statusCode === 422) {
      return detail || 'Hay campos inválidos en el formulario. Revisa la información e inténtalo de nuevo.';
    }

    if (statusCode === 401 || statusCode === 403) {
      return 'No tienes permisos para realizar esta acción.';
    }

    if (statusCode === 404) {
      return 'No se encontró el recurso solicitado.';
    }

    if (statusCode === 405) {
      return 'Esta función no está habilitada en el servidor en este momento.';
    }

    if (statusCode === 409 || detailLower.includes('already exists')) {
      return 'Ya existe un usuario con ese correo electrónico.';
    }

    if (statusCode >= 500) {
      return 'Ocurrió un problema interno del servidor. Intenta nuevamente en unos segundos.';
    }

    if (detail) {
      return detail;
    }

    return fallbackMessage;
  }, []);

  const updateNavigationContext = useCallback((tab: 'analytics' | 'prompts' | 'users') => {
    const navigationItems = [
      {
        id: 'admin',
        label: 'Administración',
        path: '/admin',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"></path>
          </svg>
        )
      },
      {
        id: 'analytics',
        label: 'Analytics',
        parent: 'admin',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
            <polyline points="17 6 23 6 23 12"></polyline>
          </svg>
        )
      },
      {
        id: 'prompts',
        label: 'Prompts',
        parent: 'admin',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
        )
      },
      {
        id: 'users',
        label: 'Usuarios',
        parent: 'admin',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        )
      }
    ];

    setNavigationContext(navigationItems, tab);
  }, [setNavigationContext]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardStats, healthData, conversationData, advancedData] = await Promise.all([
        apiClient.getDashboardStats(),
        apiClient.getSystemHealth(),
        apiClient.getAllConversations(),
        apiClient.getAdvancedAnalytics(analyticsRange),
      ]);

      setStats(dashboardStats);
      setHealth(healthData);
      setConversations(conversationData);
      setAdvancedAnalytics(advancedData);
      setError(null);
    } catch (err: any) {
      setError(getFriendlyErrorMessage(err, 'No se pudieron cargar los datos de analytics.'));
    } finally {
      setLoading(false);
    }
  }, [getFriendlyErrorMessage, analyticsRange]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!user?.is_admin) {
      navigate('/');
      return;
    }

    loadDashboardData();
  }, [isAuthenticated, user, navigate, loadDashboardData]);

  // Set initial navigation context
  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      updateNavigationContext(activeTab);
    }
  }, [activeTab, isAuthenticated, user, updateNavigationContext]);

  const loadTabData = useCallback(async (tab: string) => {
    try {
      switch (tab) {
        case 'analytics':
          const [analyticsStats, analyticsHealth, conversationData, advancedData] = await Promise.all([
            apiClient.getDashboardStats(),
            apiClient.getSystemHealth(),
            apiClient.getAllConversations(),
            apiClient.getAdvancedAnalytics(analyticsRange),
          ]);
          setStats(analyticsStats);
          setHealth(analyticsHealth);
          setConversations(conversationData);
          setAdvancedAnalytics(advancedData);
          break;
        case 'prompts':
          const promptData = await apiClient.getPrompts();
          setPrompts(promptData);
          if (promptData.length > 0 && !selectedPrompt) {
            setSelectedPrompt(promptData[0]);
          }
          break;
        case 'users':
          const userData = await apiClient.getAllUsers();
          setUsers(userData);
          break;
      }
      setError(null);
    } catch (err: any) {
      setError(getFriendlyErrorMessage(err, 'No se pudieron cargar los datos de la pestaña seleccionada.'));
    }
  }, [analyticsRange, getFriendlyErrorMessage, selectedPrompt]);

  useEffect(() => {
    if (activeTab !== 'analytics' || !isAuthenticated || !user?.is_admin) return;
    loadTabData('analytics');
  }, [analyticsRange, activeTab, isAuthenticated, user, loadTabData]);

  const handlePromptSelect = async (promptKey: string) => {
    try {
      const prompt = await apiClient.getPrompt(promptKey);
      setSelectedPrompt(prompt);
    } catch (err: any) {
      setError(getFriendlyErrorMessage(err, 'No se pudo cargar el prompt seleccionado.'));
    }
  };

  const handlePromptSave = async (promptKey: string, updates: any) => {
    try {
      setSaving(true);

      // Update existing prompt
      await apiClient.updatePrompt(promptKey, updates);
      
      // Refresh the prompt list
      const promptData = await apiClient.getPrompts();
      setPrompts(promptData);
      
      // Update the selected prompt
      if (selectedPrompt?.prompt_key === promptKey) {
        const updatedPrompt = await apiClient.getPrompt(promptKey);
        setSelectedPrompt(updatedPrompt);
      }
      
      setError(null);
    } catch (err: any) {
      setError(getFriendlyErrorMessage(err, 'No se pudieron guardar los cambios del prompt.'));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleUserToggle = async (userId: string) => {
    try {
      await apiClient.toggleUserActive(userId);
      const userData = await apiClient.getAllUsers();
      setUsers(userData);
    } catch (err: any) {
      setError(getFriendlyErrorMessage(err, 'No se pudo actualizar el estado del usuario.'));
    }
  };

  const handleUserFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setSaving(true);
      await apiClient.createUser(userFormData);
      
      // Reset form
      setUserFormData({
        email: '',
        password: '',
        is_admin: false,
        is_active: true
      });
      setShowUserForm(false);
      
      // Refresh users list
      const userData = await apiClient.getAllUsers();
      setUsers(userData);
      setError(null);
    } catch (err: any) {
      setError(getFriendlyErrorMessage(err, 'No se pudo crear el usuario.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUserFormChange = (field: string, value: string | boolean) => {
    setUserFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTabChange = (tab: 'analytics' | 'prompts' | 'users') => {
    setActiveTab(tab);
    loadTabData(tab);
    updateNavigationContext(tab);
  };

  const normalizeHealthStatus = (status?: string) => (status || '').trim().toLowerCase();

  const isHealthyStatus = (status?: string) => {
    const normalized = normalizeHealthStatus(status);
    return ['ok', 'healthy', 'connected', 'available'].includes(normalized);
  };

  const getHealthClass = (status: string) => {
    const normalized = normalizeHealthStatus(status);
    if (isHealthyStatus(normalized)) return 'health-ok';
    if (normalized === 'degraded') return 'health-warn';
    return 'health-error';
  };

  const getConversationStatusLabel = (status: string) => {
    const normalized = (status || '').trim().toLowerCase();

    switch (normalized) {
      case 'active':
        return 'Activo';
      case 'completed':
        return 'Completado';
      case 'paused':
        return 'Pausado';
      case 'abandoned':
        return 'Abandonado';
      case 'inactive':
        return 'Inactivo';
      default:
        return status;
    }
  };

  const getFlowValueLabel = (stage: string, value: string) => {
    const normalized = (value || '').trim().toLowerCase();

    if (!normalized) {
      return 'No disponible';
    }

    if (stage === 'status') {
      return getConversationStatusLabel(normalized);
    }

    if (normalized === 'active') return 'Activo';
    if (normalized === 'inactive') return 'Inactivo';

    return value;
  };

  const getConversationOwnerName = (conversation: ConversationSummary) => {
    const email = (conversation.user_email || '').trim();
    return email || `Usuario ${conversation.user_id.slice(0, 8)}`;
  };

  const getConversationOwnerIdLabel = (conversation: ConversationSummary) => {
    if (!conversation.user_id) return 'Sin ID';
    return `ID: ${conversation.user_id}`;
  };

  const normalizedConversationQuery = conversationQuery.trim().toLowerCase();
  const topFunctions = advancedAnalytics?.top_functions || [];
  const topIntents = advancedAnalytics?.top_intents || [];
  const topRequests = advancedAnalytics?.top_user_requests || [];
  const workflowDistribution = advancedAnalytics?.workflow_distribution || [];
  const flow = advancedAnalytics?.flow;
  const flowStages = flow?.stage_breakdown || [];
  const flowTransitions = flow?.transitions || [];
  const totalSessionsAnalyzed = advancedAnalytics?.total_sessions_analyzed || 0;

  const stageLabels: Record<string, string> = {
    channel: 'Canal',
    intent: 'Intención',
    workflow: 'Workflow',
    function: 'Función',
    status: 'Estado',
  };

  const stageOrder = ['channel', 'intent', 'workflow', 'function', 'status'];

  const stageGroups = stageOrder
    .map((stage) => {
      const nodes = flowStages
        .filter((item) => item.stage === stage)
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
      return {
        stage,
        label: stageLabels[stage],
        nodes,
      };
    })
    .filter((group) => group.nodes.length > 0);

  const transitionGroups = stageOrder
    .slice(0, -1)
    .map((fromStage, idx) => {
      const toStage = stageOrder[idx + 1];
      const items = flowTransitions
        .filter((item) => item.from_stage === fromStage && item.to_stage === toStage)
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
      const maxCount = items.reduce((max, current) => Math.max(max, current.count), 0);

      return {
        key: `${fromStage}-${toStage}`,
        fromStage,
        toStage,
        fromLabel: stageLabels[fromStage],
        toLabel: stageLabels[toStage],
        items,
        maxCount,
      };
    })
    .filter((group) => group.items.length > 0);

  const intelligenceSections = [
    {
      key: 'functions',
      title: 'Funciones Más Usadas',
      sourceLabel: 'Fuente: metadata.executed_function y metadata.tool_calls',
      coverageLabel: `${flow?.sessions_with_function_calls || 0}/${totalSessionsAnalyzed} sesiones con funciones`,
      items: topFunctions,
      requestStyle: false,
    },
    {
      key: 'intents',
      title: 'Intenciones Detectadas',
      sourceLabel: 'Fuente: state_data.intent.detected',
      coverageLabel: `${flow?.sessions_with_intent || 0}/${totalSessionsAnalyzed} sesiones con intención`,
      items: topIntents,
      requestStyle: false,
    },
    {
      key: 'requests',
      title: 'Qué Están Pidiendo',
      sourceLabel: 'Fuente: mensajes de usuario (role=user) normalizados',
      coverageLabel: `${topRequests.reduce((acc, item) => acc + item.count, 0)} peticiones agrupadas`,
      items: topRequests,
      requestStyle: true,
    },
    {
      key: 'workflows',
      title: 'Workflows Más Usados',
      sourceLabel: 'Fuente: state_data.workflow.active',
      coverageLabel: `${flow?.sessions_with_workflow || 0}/${totalSessionsAnalyzed} sesiones con workflow`,
      items: workflowDistribution,
      requestStyle: false,
    },
  ];

  const visibleIntelligenceSections = intelligenceSections.filter((section) => section.items.length > 0);

  const filteredConversations = conversations.filter((conversation) => {
    if (!normalizedConversationQuery) return true;

    const statusLabel = getConversationStatusLabel(conversation.status).toLowerCase();
    const candidateFields = [
      conversation.user_email,
      conversation.user_id,
      conversation.session_id,
      conversation.channel,
      conversation.status,
      statusLabel,
      conversation.last_message || '',
    ]
      .map((value) => (value || '').toLowerCase());

    return candidateFields.some((value) => value.includes(normalizedConversationQuery));
  });

  const visibleConversations = showAllConversations
    ? filteredConversations
    : filteredConversations.slice(0, 8);
  const hasMoreConversations = filteredConversations.length > 8;

  const filteredUsers = users.filter((currentUser) => {
    const roleMatches =
      userRoleFilter === 'all' ||
      (userRoleFilter === 'admin' && currentUser.is_admin) ||
      (userRoleFilter === 'user' && !currentUser.is_admin);

    const statusMatches =
      userStatusFilter === 'all' ||
      (userStatusFilter === 'active' && currentUser.is_active) ||
      (userStatusFilter === 'inactive' && !currentUser.is_active);

    return roleMatches && statusMatches;
  });

  const promptCategories = Array.from(
    new Set(prompts.map((prompt) => prompt.category).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));

  const filteredPrompts = prompts.filter((prompt) => {
    if (promptCategoryFilter === 'all') return true;
    return prompt.category === promptCategoryFilter;
  });

  useEffect(() => {
    if (activeTab !== 'prompts') return;

    if (filteredPrompts.length === 0) {
      setSelectedPrompt(null);
      return;
    }

    const hasSelectedPrompt = selectedPrompt
      ? filteredPrompts.some((prompt) => prompt.prompt_key === selectedPrompt.prompt_key)
      : false;

    if (!hasSelectedPrompt) {
      setSelectedPrompt(filteredPrompts[0]);
    }
  }, [activeTab, filteredPrompts, selectedPrompt]);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-large"></div>
        <p>Cargando panel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-loading">
        <div className="admin-error-box">
          <p>{error}</p>
          <button onClick={loadDashboardData} className="admin-retry-btn">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-inner">
          <h1>Panel de Administracion</h1>
          <button onClick={() => navigate('/')} className="admin-back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Volver
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="admin-tabs">
        <div className="admin-tabs-inner">
          {(['analytics', 'prompts', 'users'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`admin-tab ${activeTab === tab ? 'active' : ''}`}
            >
              {tab === 'analytics' && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
              )}
              {tab === 'prompts' && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              )}
              {tab === 'users' && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              )}
              <span>{
                tab === 'analytics' ? 'Analytics' :
                tab === 'prompts' ? 'Prompts' : 'Usuarios'
              }</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="admin-content">
        {activeTab === 'analytics' && (
          <div className="analytics-content">
            <div className="admin-card analytics-controls-card">
              <div className="analytics-controls-row">
                <div className="analytics-subtabs" aria-label="Pestañas de analytics">
                  <button
                    type="button"
                    className={`analytics-subtab ${analyticsTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setAnalyticsTab('overview')}
                  >
                    Vista General
                  </button>
                  <button
                    type="button"
                    className={`analytics-subtab ${analyticsTab === 'flow' ? 'active' : ''}`}
                    onClick={() => setAnalyticsTab('flow')}
                  >
                    Flujo Operacional
                  </button>
                </div>

                <div className="analytics-range-controls">
                  <label htmlFor="admin-analytics-range">Rango</label>
                  <select
                    id="admin-analytics-range"
                    value={analyticsRange}
                    onChange={(e) => setAnalyticsRange(e.target.value as '24h' | '7d' | '30d' | '90d')}
                  >
                    <option value="24h">24 horas</option>
                    <option value="7d">7 días</option>
                    <option value="30d">30 días</option>
                    <option value="90d">90 días</option>
                  </select>
                </div>
              </div>
            </div>

            {analyticsTab === 'overview' ? (
              <>
            {/* System Health Overview */}
            <div className="admin-card system-overview-card">
              <div className="card-header-row">
                <h3 className="card-title">Estado del Sistema</h3>
                {health && (
                  <div className="system-status">
                    <div className={`status-indicator ${isHealthyStatus(health.status) ? 'healthy' : 'unhealthy'}`}></div>
                    <span className="status-text">{isHealthyStatus(health.status) ? 'Operativo' : 'Problemas'}</span>
                  </div>
                )}
              </div>
              {health && (
                <div className="health-grid">
                  <div className={`health-item ${getHealthClass(health.database)}`}>
                    <span className="health-label">Base de Datos</span>
                    <span className="health-value">{health.database}</span>
                  </div>
                  <div className={`health-item ${getHealthClass(health.redis)}`}>
                    <span className="health-label">Redis</span>
                    <span className="health-value">{health.redis}</span>
                  </div>
                  <div className={`health-item ${getHealthClass(health.storage)}`}>
                    <span className="health-label">Almacenamiento</span>
                    <span className="health-value">{health.storage}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Key Metrics */}
            <div className="stats-grid analytics-stats-grid">
              <AnalyticsCard
                title="Usuarios Totales"
                value={stats?.total_users || 0}
                subtitle={`${stats?.new_users_today || 0} nuevos hoy`}
                icon="users"
                color="#00d4aa"
                size="large"
              />
              
              <AnalyticsCard
                title="Conversaciones"
                value={stats?.total_conversations || 0}
                subtitle={`${stats?.active_conversations || 0} activas`}
                icon="chat"
                color="#00d4aa"
                size="large"
              />
              
              <AnalyticsCard
                title="Mensajes Hoy"
                value={stats?.messages_today || 0}
                subtitle="Interacciones diarias"
                icon="message"
                color="#00d4aa"
                size="large"
              />

              <AnalyticsCard
                title="Tool Calls"
                value={advancedAnalytics?.total_tool_calls || 0}
                subtitle={`${advancedAnalytics?.total_sessions_analyzed || 0} sesiones analizadas`}
                icon="activity"
                color="#2ec4b6"
                size="large"
              />

              <AnalyticsCard
                title="Mensajes Promedio"
                value={advancedAnalytics?.average_messages_per_conversation || 0}
                subtitle="por conversación"
                icon="clock"
                color="#ff9f6b"
                size="large"
              />
            </div>

            {/* Recent Conversations Table */}
            <div className="admin-card recent-conversations-card">
              <div className="card-header-row">
                <h3 className="card-title">
                  Conversaciones Recientes
                  <span className="conversations-count-chip">
                    {visibleConversations.length}/{filteredConversations.length}
                  </span>
                </h3>
                {hasMoreConversations ? (
                  <button
                    className="admin-link-btn"
                    onClick={() => setShowAllConversations((prev) => !prev)}
                  >
                    {showAllConversations ? 'Ver menos' : 'Ver todas'}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                ) : (
                  <span className="admin-link-btn" aria-disabled="true">
                    Mostrando todas
                  </span>
                )}
              </div>
              <div className="conversations-toolbar">
                <label className="conversations-search" htmlFor="conversation-search-input">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    id="conversation-search-input"
                    type="text"
                    value={conversationQuery}
                    onChange={(e) => {
                      setConversationQuery(e.target.value);
                      setShowAllConversations(false);
                    }}
                    placeholder="Buscar por dueño, ID, sesión, canal o estado"
                  />
                </label>
                <span className="conversations-match-count">
                  {filteredConversations.length} resultado{filteredConversations.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="conversations-table">
                <table>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Canal</th>
                      <th>Estado</th>
                      <th>Mensajes</th>
                      <th>Actualizado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleConversations.map(conv => (
                      <tr key={conv.session_id}>
                        <td className="conversation-owner-cell">
                          <div className="conversation-owner-avatar">
                            {getConversationOwnerName(conv).charAt(0).toUpperCase()}
                          </div>
                          <div className="conversation-owner-details">
                            <span className="conversation-owner-name">{getConversationOwnerName(conv)}</span>
                            <span className="conversation-owner-id">{getConversationOwnerIdLabel(conv)}</span>
                          </div>
                        </td>
                        <td>
                          <span className="channel-badge">{conv.channel}</span>
                        </td>
                        <td>
                          <span className={`status-badge status-${conv.status}`}>
                            {getConversationStatusLabel(conv.status)}
                          </span>
                        </td>
                        <td>{conv.message_count}</td>
                        <td className="timestamp">
                          {new Date(conv.updated_at).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                      </tr>
                    ))}
                    {visibleConversations.length === 0 && (
                      <tr>
                        <td colSpan={5} className="conversations-empty-state">
                          No hay conversaciones que coincidan con la búsqueda.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {visibleIntelligenceSections.length > 0 && (
              <div className="admin-card advanced-analytics-card">
                <h3 className="card-title">Inteligencia Adicional</h3>
                <div className="advanced-grid">
                  {visibleIntelligenceSections.map((section) => (
                    <div className="advanced-list-box" key={section.key}>
                      <div className="advanced-list-header">
                        <h4>{section.title}</h4>
                        <span className="advanced-coverage">{section.coverageLabel}</span>
                      </div>
                      <p className="advanced-source">{section.sourceLabel}</p>
                      <ul className="advanced-list">
                        {section.items.slice(0, 8).map((item) => (
                          <li key={`${section.key}-${item.name}`}>
                            <span className={section.requestStyle ? 'advanced-request' : ''}>{item.name}</span>
                            <strong>{item.count}</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

              </>
            ) : (
              <div className="admin-card flow-analytics-card">
                <h3 className="card-title">Flujo Operacional</h3>
                <p className="card-description">
                  Visualización real de transiciones por sesión entre etapas: canal, función y estado.
                </p>

                <div className="flow-funnel-grid">
                  <div className="flow-funnel-card">
                    <span>Sesiones analizadas</span>
                    <strong>{totalSessionsAnalyzed}</strong>
                  </div>
                  <div className="flow-funnel-card">
                    <span>Con función ejecutada</span>
                    <strong>{flow?.sessions_with_function_calls || 0}</strong>
                  </div>
                </div>

                {stageGroups.length === 0 ? (
                  <p className="advanced-empty">No hay datos suficientes para construir el flujo en este rango.</p>
                ) : (
                  <div className="flow-stage-grid">
                    {stageGroups.map((group) => {
                      const stageTotal = group.nodes.reduce((acc, item) => acc + item.count, 0);
                      return (
                        <section className="flow-stage-column" key={group.stage}>
                          <header className="flow-stage-header">
                            <h4>{group.label}</h4>
                            <span>{stageTotal}</span>
                          </header>
                          <div className="flow-stage-nodes">
                            {group.nodes.map((node) => {
                              const ratio = stageTotal > 0 ? (node.count / stageTotal) * 100 : 0;
                              return (
                                <div className="flow-stage-node" key={`${group.stage}-${node.name}`}>
                                  <div className="flow-stage-node-top">
                                    <span className="flow-stage-node-name">{getFlowValueLabel(group.stage, node.name)}</span>
                                    <strong>{node.count}</strong>
                                  </div>
                                  <div className="flow-stage-bar-track">
                                    <div className="flow-stage-bar-fill" style={{ width: `${Math.max(ratio, 6)}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}

                {transitionGroups.length > 0 && (
                  <div className="flow-transition-groups">
                    {transitionGroups.map((group) => (
                      <section className="flow-transition-card" key={group.key}>
                        <header className="flow-transition-header">
                          <h4>{group.fromLabel} → {group.toLabel}</h4>
                        </header>
                        <ul className="flow-transition-list">
                          {group.items.map((item) => {
                            const ratio = group.maxCount > 0 ? (item.count / group.maxCount) * 100 : 0;
                            return (
                              <li key={`${item.from_name}-${item.to_name}`}>
                                <div className="flow-transition-row">
                                  <span className="flow-transition-path">
                                    {getFlowValueLabel(group.fromStage, item.from_name)} → {getFlowValueLabel(group.toStage, item.to_name)}
                                  </span>
                                  <strong>{item.count}</strong>
                                </div>
                                <div className="flow-transition-track">
                                  <div className="flow-transition-fill" style={{ width: `${Math.max(ratio, 6)}%` }} />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'prompts' && (
          <div className="prompts-content">
            {/* Header with statistics */}
            <div className="admin-card prompts-header-card">
              <div className="card-header-row">
                <div className="prompts-stats">
                  <h2 className="card-title">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                      <polyline points="14,2 14,8 20,8"/>
                    </svg>
                    Gestión de Prompts
                  </h2>
                  <div className="prompts-metrics">
                    <div className="prompt-metric">
                      <span className="metric-value">{prompts.length}</span>
                      <span className="metric-label">Total</span>
                    </div>
                    <div className="prompt-metric">
                      <span className="metric-value">{prompts.filter(p => p.is_active).length}</span>
                      <span className="metric-label">Activos</span>
                    </div>
                    <div className="prompt-metric">
                      <span className="metric-value">{promptCategories.length}</span>
                      <span className="metric-label">Categorías</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="prompts-layout">
              <div className="prompts-sidebar">
                <div className="prompts-sidebar-header">
                  <h3>Prompts disponibles</h3>
                  <span className="prompt-count">{filteredPrompts.length}</span>
                </div>
                
                <div className="prompt-filters">
                  <select
                    className="prompt-filter-select"
                    value={promptCategoryFilter}
                    onChange={(e) => setPromptCategoryFilter(e.target.value)}
                  >
                    <option value="all">Todas las categorías</option>
                    {promptCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="prompt-list">
                  {filteredPrompts.map(prompt => (
                    <div
                      key={prompt.prompt_key}
                      className={`prompt-item ${selectedPrompt?.prompt_key === prompt.prompt_key ? 'selected' : ''}`}
                      onClick={() => handlePromptSelect(prompt.prompt_key)}
                    >
                      <div className="prompt-item-header">
                        <div className="prompt-name">{prompt.name}</div>
                        <div className="prompt-status">
                          <div className={`status-indicator ${prompt.is_active ? 'active' : 'inactive'}`}></div>
                        </div>
                      </div>
                      
                      <div className="prompt-meta">
                        <span className="prompt-category-tag">{prompt.category}</span>
                        <span className="prompt-key">{prompt.prompt_key}</span>
                      </div>
                      
                      <div className="prompt-description">
                        {prompt.description?.length > 80 
                          ? `${prompt.description.substring(0, 80)}...` 
                          : prompt.description || 'Sin descripción'
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="prompts-main">
                {selectedPrompt ? (
                  <PromptEditor
                    prompt={selectedPrompt}
                    onSave={handlePromptSave}
                    saving={saving}
                  />
                ) : (
                  <div className="no-prompt-selected">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <h3>Selecciona un prompt</h3>
                    <p>Elige un prompt de la lista para comenzar a editarlo</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-content">
            {/* Header with user statistics */}
            <div className="admin-card users-header-card">
              <div className="card-header-row">
                <div className="users-stats">
                  <h2 className="card-title">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    Gestión de Usuarios
                  </h2>
                  <div className="users-metrics">
                    <div className="user-metric">
                      <span className="metric-value">{users.length}</span>
                      <span className="metric-label">Total</span>
                    </div>
                    <div className="user-metric">
                      <span className="metric-value">{users.filter(u => u.is_active).length}</span>
                      <span className="metric-label">Activos</span>
                    </div>
                    <div className="user-metric">
                      <span className="metric-value">{users.filter(u => u.is_admin).length}</span>
                      <span className="metric-label">Admins</span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => setShowUserForm(!showUserForm)}
                  className="admin-action-btn"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  {showUserForm ? 'Cancelar' : 'Crear Usuario'}
                </button>
              </div>
            </div>

            {showUserForm && (
              <div className="user-create-form">
                <h3>Crear Nuevo Usuario</h3>
                <form onSubmit={handleUserFormSubmit}>
                  <div className="user-form-grid">
                    <div className="input-group">
                      <label className="input-label">Email</label>
                      <input 
                        type="email" 
                        value={userFormData.email}
                        onChange={(e) => handleUserFormChange('email', e.target.value)}
                        placeholder="usuario@empresa.com"
                        className="input"
                        required
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Contraseña</label>
                      <input 
                        type="password" 
                        value={userFormData.password}
                        onChange={(e) => handleUserFormChange('password', e.target.value)}
                        placeholder="Contraseña segura"
                        className="input"
                        required
                      />
                    </div>
                  </div>
                  <div className="user-form-grid">
                    <label className="checkbox-label">
                      <input 
                        type="checkbox" 
                        checked={userFormData.is_admin}
                        onChange={(e) => handleUserFormChange('is_admin', e.target.checked)}
                      />
                      <span>Usuario administrador</span>
                    </label>
                    <label className="checkbox-label">
                      <input 
                        type="checkbox" 
                        checked={userFormData.is_active}
                        onChange={(e) => handleUserFormChange('is_active', e.target.checked)}
                      />
                      <span>Usuario activo</span>
                    </label>
                  </div>
                  <div className="user-form-actions">
                    <button 
                      type="button" 
                      onClick={() => setShowUserForm(false)}
                      className="admin-action-btn"
                      style={{ background: 'transparent', color: 'var(--dark-text-muted)' }}
                    >
                      Cancelar
                    </button>
                    <button 
                      type="submit" 
                      disabled={saving || !userFormData.email || !userFormData.password}
                      className="admin-action-btn"
                    >
                      {saving ? 'Creando...' : 'Crear Usuario'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="admin-card users-table-card">
              <div className="card-header-row">
                <h3 className="card-title">Lista de Usuarios</h3>
                <div className="users-table-filters">
                  <select
                    className="filter-select"
                    value={userRoleFilter}
                    onChange={(e) => setUserRoleFilter(e.target.value as 'all' | 'admin' | 'user')}
                  >
                    <option value="all">Todos los roles</option>
                    <option value="admin">Administradores</option>
                    <option value="user">Usuarios</option>
                  </select>
                  <select
                    className="filter-select"
                    value={userStatusFilter}
                    onChange={(e) => setUserStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                  >
                    <option value="all">Todos los estados</option>
                    <option value="active">Activos</option>
                    <option value="inactive">Inactivos</option>
                  </select>
                </div>
              </div>
              
              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Rol</th>
                      <th>Estado</th>
                      <th>Conversaciones</th>
                      <th>Último Acceso</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.user_id} className="user-table-row">
                        <td className="user-info-cell">
                          <div className="user-avatar">
                            {user.email.charAt(0).toUpperCase()}
                          </div>
                          <div className="user-details">
                            <span className="user-email">{user.email}</span>
                            <span className="user-id">ID: {user.user_id}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`role-badge ${user.is_admin ? 'admin' : 'user'}`}>
                            {user.is_admin ? 'Administrador' : 'Usuario'}
                          </span>
                        </td>
                        <td>
                          <span className={`user-status-pill ${user.is_active ? 'active' : 'inactive'}`}>
                            {user.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="conversations-cell">
                          <span className="conversation-count">{user.conversation_count || 0}</span>
                        </td>
                        <td className="last-access-cell">
                          <span className="access-time">
                            {user.last_login_at 
                              ? new Date(user.last_login_at).toLocaleDateString('es-ES', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'Nunca'
                            }
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleUserToggle(user.user_id)}
                            className={`user-action-btn ${user.is_active ? 'deactivate' : 'activate'}`}
                            title={user.is_active ? 'Desactivar usuario' : 'Activar usuario'}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              {user.is_active ? (
                                <path d="M18 6L6 18M6 6l12 12"></path>
                              ) : (
                                <path d="M9 12l2 2 4-4"></path>
                              )}
                            </svg>
                            {user.is_active ? 'Desactivar' : 'Activar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan={6} className="users-empty-state">
                          No hay usuarios que coincidan con los filtros seleccionados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
